/**
 * Dosyalar / Çıktılar - the artifact and export centre.
 *
 * ## What this screen is for, and what it is not
 *
 * Three project screens divide the work and must keep dividing it:
 *
 *   3D Rekonstrüksiyon  - *produce*. The button that starts a run lives there.
 *   3D Model            - *inspect*. The viewer and the stage states.
 *   Dosyalar / Çıktılar - *manage*. What exists, how big it is, download it.
 *
 * So there is no "generate" action anywhere below. Putting one here would give
 * the same operation two homes and make it unclear which one is authoritative.
 *
 * ## Two defects this rewrite fixes
 *
 * **It was an N+1.** Each capture session row fetched its own media list, so a
 * project with twenty sessions made twenty-one requests to draw one table.
 * The counts now come from the project list aggregate, which the rest of the
 * product already uses, at a cost of one request that does not grow.
 *
 * **It said downloads did not exist.** The footer read "Bu sürümde dosya
 * indirme uç noktası yok" - copy written before the artifact download route
 * shipped, left standing after it did. Denying a capability the product has is
 * the same class of defect as inventing one, and this file had the deployed
 * example of it.
 *
 * ## Stage-aware, and future-artifact-shaped
 *
 * The empty state is not one stale sentence. It names the furthest real output
 * and what is genuinely missing after it, driven by `furthest_output` from the
 * shared workflow model. When a dense cloud or a mesh eventually exists, the
 * section renders it from `ARTIFACT_KINDS` - keyed on the artifact *kind* the
 * pipeline stamped, never on a filename or an extension - and the empty state
 * moves one rung up the ladder without a second redesign.
 */

import { useCallback, useState } from "react";
import {
  getLatestReconstruction,
  getProjectWorkflow,
  getScaleCalibration,
} from "@/api/client";
import type { ProjectWorkflow, ReconstructionArtifact } from "@/api/types";
import { ErrorState } from "@/components/ErrorState";
import { IconLayers } from "@/components/icons";
import { PageHeader, Section } from "@/components/product";
import { Button, Callout, EmptyState, Loading, StatusBadge } from "@/components/ui";
import { formatDateTime } from "@/lib/format";
import { useAsync } from "@/lib/useAsync";
import { useProjectContext } from "@/features/projects/ProjectScreen";
import { downloadArtifact, downloadFailureText } from "./artifactDownload";
import { ModelDownloads } from "./ModelDownloads";
import "./reconstruction.css";

/**
 * What each artifact kind is called, and what it does *not* mean.
 *
 * Keyed on the enum the pipeline stamps at the stage that wrote the file, so
 * a future dense or mesh artifact appears here with its real name the moment
 * one is registered. Everything listed here is downloadable; anything that is
 * not is not in the table at all - see `DIAGNOSTIC_KINDS`.
 */
const ARTIFACT_KINDS: Record<string, { label: string; note?: string }> = {
  sparse_point_cloud: {
    label: "Seyrek nokta bulutu (PLY)",
    note: "Kameraların çözdüğü 3D noktalar. Yüzey modeli değildir. Çoğu 3D uygulamasında açılır.",
  },
  camera_poses: {
    label: "Kamera konumları",
    note: "Her fotoğrafın çözülmüş konumu ve yönü. Metin dosyası; 3D yazılımlarda içe aktarılabilir.",
  },
  dense_point_cloud: {
    label: "Yoğun nokta bulutu (PLY)",
    note: "Çok daha fazla nokta; hâlâ bir yüzey modeli değildir.",
  },
  mesh: { label: "Yüzey (mesh)", note: "Bağlı yüzey. Paketlenmiş 3D modelden daha büyüktür." },
  textured_mesh: {
    label: "Dokulu 3D model (GLB)",
    note: "Fotoğraflardan çıkarılan doku yüzeye yansıtılmış hâli. Doku atlasları dosyanın içinde gömülüdür; ayrıca bir resim dosyası gerekmez.",
  },
  model_glb: {
    label: "3D model (GLB)",
    // What it is and what it is not. Texturing runs after this file is already
    // written, so this one carries per-vertex colour and the textured model is
    // a separate row - which is the honest split, because a capture whose
    // texturing did not run still produces this one.
    note: "Web tarayıcılarında ve birçok 3D uygulamasında açılır. Renk yüzeye gömülüdür. Doku atlası içeren sürüm, varsa ayrı satırda listelenir.",
  },
};

/**
 * Artifacts that exist on the server and are not the customer's result.
 *
 * The engine log used to appear in this table with a dash where its action
 * should be - a row that says "there is a file and you may do nothing with
 * it". It is raw engine output: it can carry a worker path or a command line,
 * so it is not ours to hand over, and a row nobody can act on is worse than an
 * honest sentence saying the log is kept for diagnostics. That sentence is
 * below the table.
 */
const DIAGNOSTIC_KINDS = new Set(["engine_log"]);

/** Which artifact kinds belong under which heading. */
const RECONSTRUCTION_KINDS = new Set([
  "sparse_point_cloud",
  "camera_poses",
  "dense_point_cloud",
]);
const MODEL_KINDS = new Set(["mesh", "textured_mesh", "model_glb"]);

function fileSize(bytes: number): string {
  if (bytes >= 1e9) return `${(bytes / 1e9).toFixed(2)} GB`;
  if (bytes >= 1e6) return `${(bytes / 1e6).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1e3))} KB`;
}

/**
 * The sentence for "there is nothing here yet", chosen by what *is* here.
 *
 * The rung above the furthest real output, named honestly. A sparse-only
 * project is told a sparse cloud is ready and a final model is not - never
 * one flat "bu sürümde 3D model yok", which stays on screen unchanged whether
 * the project has nothing or has everything but the last stage.
 */
export function outputStageMessage(
  workflow: ProjectWorkflow | null,
  status?: string | null,
): string {
  // What the run is doing beats what it has produced, when it has produced
  // nothing yet. "Henüz rekonstrüksiyon çıktısı yok" is true of a job that is
  // running and reads as though nothing is happening.
  if (!workflow?.furthest_output) {
    if (status === "running") return "3D çalışması sürüyor. Bittiğinde çıktılar burada listelenir.";
    if (status === "queued") return "3D çalışması sırada. Başladığında ilerlemesini görebilirsiniz.";
    if (status === "failed") return "Son 3D çalışması tamamlanamadı, bu yüzden çıktı üretilmedi.";
  }
  switch (workflow?.furthest_output) {
    case "texture":
      return "Dokulu 3D model hazır.";
    case "mesh":
      return "3D model hazır. Doku henüz oluşturulmadı.";
    case "dense":
      return "Yoğun nokta bulutu hazır. Mesh henüz oluşturulmadı.";
    case "sparse":
      return "Seyrek 3D hazır. Final 3D model henüz oluşturulmadı.";
    default:
      return "Henüz rekonstrüksiyon çıktısı yok.";
  }
}

/**
 * One artifact, with a real download.
 *
 * The bytes are fetched through the API client - which carries the session
 * cookie and re-checks ownership server-side - and handed to the browser as a
 * blob URL that is revoked immediately. There is no direct link to the
 * endpoint, so no URL that could be copied out of the page and shared, and
 * **no storage key or path appears anywhere**: the row knows an artifact id
 * and a kind, and that is all the API returns.
 */
function ArtifactRow({
  artifact,
  jobId,
}: {
  artifact: ReconstructionArtifact;
  jobId: string;
}) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const kind = ARTIFACT_KINDS[artifact.artifact_kind];

  async function download() {
    if (busy) return;
    setBusy(true);
    setFailed(null);
    try {
      await downloadArtifact(
        jobId,
        artifact.id,
        `${artifact.artifact_kind}.${artifact.artifact_format}`,
      );
    } catch (error) {
      setFailed(downloadFailureText(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <tr>
      <td>
        <div className="file-row__name">
          <IconLayers size={18} />
          <div>
            <p>{kind?.label ?? "Çıktı dosyası"}</p>
            <p className="muted small">
              {artifact.artifact_format.toUpperCase()}
              {kind?.note ? ` · ${kind.note}` : ""}
            </p>
            {failed && (
              <p className="muted small" role="alert">
                {failed}
              </p>
            )}
          </div>
        </div>
      </td>
      <td>
        <StatusBadge tone="success">Hazır</StatusBadge>
      </td>
      <td>{formatDateTime(artifact.created_at)}</td>
      <td className="numeric">{fileSize(artifact.byte_size)}</td>
      <td className="numeric">
        {/*
          Every row in this table is downloadable. Anything that is not is not
          in the table - a row with a dash where its action belongs is a file
          the product shows you and will not give you.
        */}
        <Button
          variant="secondary"
          onClick={() => void download()}
          busy={busy}
          ariaLabel={`${kind?.label ?? "Çıktı"} dosyasını indir`}
        >
          İndir
        </Button>
      </td>
    </tr>
  );
}

function ArtifactTable({
  artifacts,
  jobId,
}: {
  artifacts: ReconstructionArtifact[];
  jobId: string;
}) {
  return (
    <table className="file-table">
      <thead>
        <tr>
          <th>Çıktı</th>
          <th>Durum</th>
          <th>Tarih</th>
          <th className="numeric">Boyut</th>
          <th className="numeric">İşlem</th>
        </tr>
      </thead>
      <tbody>
        {artifacts.map((artifact) => (
          <ArtifactRow key={artifact.id} artifact={artifact} jobId={jobId} />
        ))}
      </tbody>
    </table>
  );
}

export function ProjectFilesTab() {
  const { project } = useProjectContext();

  // The source-media counts ride on this project's own workflow response.
  //
  // The first version of this rewrite read them out of `listProjects({limit:
  // 200})` and picked this project's row. That was one request rather than one
  // per session - the N+1 was gone - but it was still wrong: an account with
  // more than 200 projects would not find its row at all, and the screen would
  // confidently report "no source files" for a project full of them.
  const workflowFetcher = useCallback(
    (signal: AbortSignal) => getProjectWorkflow(project.id, { signal }),
    [project.id],
  );
  const reconstructionFetcher = useCallback(
    (signal: AbortSignal) => getLatestReconstruction(project.id, { signal }),
    [project.id],
  );

  const { state: workflowState, reload } = useAsync(workflowFetcher);
  const { state: reconstruction } = useAsync(reconstructionFetcher);
  //: This run's stored calibration, so the sentence about scale under the
  //: download list is about *this* model rather than about photogrammetry.
  //: Idle - and therefore null - until there is a job to ask about.
  const latestJobId =
    reconstruction.status === "success" ? reconstruction.data?.id ?? null : null;
  const { state: scale } = useAsync(
    useCallback(
      (signal: AbortSignal) =>
        latestJobId
          ? getScaleCalibration(latestJobId, { signal })
          : Promise.resolve(null),
      [latestJobId],
    ),
  );

  if (workflowState.status === "loading" && workflowState.data === null) {
    return <Loading />;
  }
  if (workflowState.status === "error") {
    return <ErrorState error={workflowState.error} onRetry={reload} />;
  }

  const workflow = workflowState.status === "success" ? workflowState.data : null;
  const row = workflow;
  // A 404 here is "no reconstruction yet", which is an absence rather than an
  // error, so it renders as an empty section.
  const job = reconstruction.status === "success" ? reconstruction.data : null;
  const artifacts = job?.artifacts ?? [];

  const reconstructionArtifacts = artifacts.filter((artifact) =>
    RECONSTRUCTION_KINDS.has(artifact.artifact_kind),
  );
  const modelArtifacts = artifacts.filter((artifact) =>
    MODEL_KINDS.has(artifact.artifact_kind),
  );
  const hasDiagnostics = artifacts.some((artifact) =>
    DIAGNOSTIC_KINDS.has(artifact.artifact_kind),
  );

  return (
    <div className="stack">
      <PageHeader
        title="Dosyalar / Çıktılar"
        subtitle={project.name}
        meta={<span>Görüntüleme, indirme ve çıktı yönetimi</span>}
      />

      <Section title="Kaynak Medya">
        {!row || row.media_count === 0 ? (
          <EmptyState
            title="Bu projede henüz kaynak dosya yok."
            description="Çekim eklendiğinde yüklenen dosyalar burada sayılır."
          />
        ) : (
          <>
            <table className="file-table">
              <thead>
                <tr>
                  <th>Tür</th>
                  <th className="numeric">Adet</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Fotoğraf</td>
                  <td className="numeric">{row.photo_count ?? 0}</td>
                </tr>
                <tr>
                  <td>Video</td>
                  <td className="numeric">{row.video_count ?? 0}</td>
                </tr>
                {(row.failed_media_count ?? 0) > 0 && (
                  <tr>
                    <td>İşlenemeyen dosya</td>
                    <td className="numeric">{row.failed_media_count}</td>
                  </tr>
                )}
              </tbody>
            </table>
            {/*
              Originals are never served back: the API has no route for them,
              and this screen must not imply one. What it can do is answer the
              question the old sentence left open - why not, and what is
              available instead.
            */}
            <Callout tone="neutral" title="Yüklediğim fotoğrafları geri indirebilir miyim?">
              Hayır. Yüklediğiniz özgün dosyalar 3D çalışması için saklanır, ancak
              geri indirme özelliği bu sürümde yok. Dosyalarınızı Çekimler bölümünde
              listeleyebilir ve büyük önizlemelerini açabilirsiniz; özgün kopyaları
              kendi cihazınızda saklamanızı öneririz.
            </Callout>
          </>
        )}
      </Section>

      <Section title="Rekonstrüksiyon Çıktıları">
        {reconstructionArtifacts.length === 0 ? (
          <EmptyState title={outputStageMessage(workflow, job?.status)} />
        ) : (
          <>
            <ArtifactTable artifacts={reconstructionArtifacts} jobId={job!.id} />
            {/*
              Answers "is the technical log one of my outputs?" once, rather
              than by listing it with nothing to press. It is raw engine output
              and can carry a worker path or a command line, so it is not ours
              to hand over.
            */}
            {hasDiagnostics && (
              <p className="muted small">
                Bu çalışma için teknik bir motor günlüğü de saklanıyor. Sorun incelemesi
                içindir, sizin çıktınız değildir ve indirilmez.
              </p>
            )}
          </>
        )}
      </Section>

      <Section title="3D Model Çıktıları">
        {modelArtifacts.length === 0 ? (
          // Stage-aware: this says what exists and what does not, rather than
          // one sentence that reads the same in every state.
          <EmptyState
            title={outputStageMessage(workflow, job?.status)}
            description="Doğrulanmış bir yüzey modeli üretildiğinde bu bölümde listelenir ve indirilebilir olur."
          />
        ) : (
          <>
            {/*
              The formats first, the files second. Somebody on this screen is
              choosing what to open the model in - GLB, OBJ or STL - and the
              artifact table answers a different question: what this run
              produced. Putting the choice above the inventory is the whole
              change; the table stays exactly as it was.
            */}
            <ModelDownloads
              jobId={job!.id}
              version={job!.version}
              scale={scale.data ?? null}
            />
            <ArtifactTable artifacts={modelArtifacts} jobId={job!.id} />
          </>
        )}
      </Section>
    </div>
  );
}
